import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Moon, Phone, Mail, Car, Clock, Trash2, Check, MessageSquare } from 'lucide-react';
import {
  adminListAfterHoursEnquiries,
  adminUpdateAfterHoursEnquiryStatus,
  adminDeleteAfterHoursEnquiry,
} from '@/lib/api';

const STATUS_META = {
  new: { label: 'New', color: 'text-green-400 border-green-700/60 bg-green-950/30' },
  contacted: { label: 'Contacted', color: 'text-blue-400 border-blue-700/60 bg-blue-950/30' },
  scheduled: { label: 'Scheduled', color: 'text-yellow-400 border-yellow-700/60 bg-yellow-950/30' },
  declined: { label: 'Declined', color: 'text-zinc-500 border-zinc-700 bg-zinc-900/30' },
};

export default function AfterHoursEnquiries({ onCountChange }) {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    adminListAfterHoursEnquiries()
      .then((res) => {
        const list = res.data || [];
        setEnquiries(list);
        if (onCountChange) onCountChange(list.filter((e) => e.status === 'new').length);
      })
      .catch(() => toast.error('Could not load after-hours enquiries'))
      .finally(() => setLoading(false));
  }, [onCountChange]);

  useEffect(load, [load]);

  const updateStatus = async (id, nextStatus) => {
    try {
      await adminUpdateAfterHoursEnquiryStatus(id, nextStatus);
      toast.success(`Marked as ${STATUS_META[nextStatus]?.label || nextStatus}`);
      load();
    } catch {
      toast.error('Could not update status');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this enquiry?')) return;
    try {
      await adminDeleteAfterHoursEnquiry(id);
      toast.success('Enquiry deleted');
      load();
    } catch {
      toast.error('Could not delete enquiry');
    }
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-NZ', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-zinc-500" data-testid="after-hours-loading">
        <Moon className="w-10 h-10 mx-auto mb-3 opacity-50 animate-pulse" />
        <p className="text-sm">Loading enquiries…</p>
      </div>
    );
  }

  if (enquiries.length === 0) {
    return (
      <div className="text-center py-16 text-zinc-500 border border-dashed border-zinc-800" data-testid="after-hours-empty">
        <Moon className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p className="font-['Barlow_Condensed'] text-lg uppercase tracking-wide text-zinc-400">
          No after-hours enquiries
        </p>
        <p className="text-xs mt-1">New customer requests will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="after-hours-list">
      {enquiries.map((e) => {
        const meta = STATUS_META[e.status] || STATUS_META.new;
        return (
          <div
            key={e.id}
            className="glass p-4 border border-zinc-800 bg-zinc-900/50"
            data-testid={`after-hours-item-${e.id}`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-white font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wide">
                    {e.name}
                  </p>
                  <span
                    className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${meta.color}`}
                    data-testid={`after-hours-status-${e.id}`}
                  >
                    {meta.label}
                  </span>
                </div>
                <p className="text-zinc-500 text-xs mt-0.5">{fmtDate(e.created_at)}</p>
              </div>
              <button
                onClick={() => remove(e.id)}
                className="text-zinc-500 hover:text-red-400 p-2 -m-2"
                data-testid={`after-hours-delete-${e.id}`}
                type="button"
                aria-label="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-4 text-sm text-zinc-300 mb-3">
              <a href={`tel:${e.phone}`} className="flex items-center gap-2 hover:text-green-400">
                <Phone className="w-4 h-4 text-zinc-500" /> {e.phone}
              </a>
              <a href={`mailto:${e.email}`} className="flex items-center gap-2 hover:text-green-400 truncate">
                <Mail className="w-4 h-4 text-zinc-500 flex-shrink-0" /> <span className="truncate">{e.email}</span>
              </a>
              <div className="flex items-center gap-2">
                <Car className="w-4 h-4 text-zinc-500" /> {e.vehicle}
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-zinc-500" /> {e.preferred_time}
              </div>
            </div>

            {e.notes && (
              <div className="flex gap-2 text-sm text-zinc-400 mb-3 p-3 bg-zinc-950/70 border border-zinc-800">
                <MessageSquare className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
                <p className="whitespace-pre-wrap break-words">{e.notes}</p>
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              {['new', 'contacted', 'scheduled', 'declined']
                .filter((s) => s !== e.status)
                .map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(e.id, s)}
                    className="text-[11px] uppercase tracking-wider px-2.5 py-1 border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500"
                    data-testid={`after-hours-set-${s}-${e.id}`}
                    type="button"
                  >
                    <Check className="w-3 h-3 inline mr-1" /> Mark {STATUS_META[s].label}
                  </button>
                ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
